import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { listPayments, type Payment } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { useAuth } from "@/contexts/AuthContext";
import { Download } from "lucide-react";

export function PaymentsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  // Moko staff = user cross-tenant (pas de merchant_id). Couvre admin, support,
  // finance-moko (rôle à venir migration 010) — le discriminant est la scope,
  // pas le rôle (car `finance` existe aussi côté tenant marchand).
  const isMokoStaff = auth.status === "authenticated" && !auth.user.merchant_id;
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState("");
  const [method, setMethod] = useState("");
  const [ticket, setTicket] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const resp = await listPayments({
        limit: 100,
        status: status || undefined,
        payment_method: method || undefined,
        ticket_number: ticket.trim() || undefined,
      });
      setPayments(resp.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch toutes les pages avec les filtres actifs (garde-fou 100 pages max)
      const all: Payment[] = [];
      let cur: string | undefined;
      for (let i = 0; i < 100; i++) {
        const resp = await listPayments({
          limit: 200,
          cursor: cur,
          status: status || undefined,
          payment_method: method || undefined,
          ticket_number: ticket.trim() || undefined,
        });
        all.push(...resp.data);
        if (!resp.has_more || !resp.next_cursor) break;
        cur = resp.next_cursor;
      }
      const rows = all.map((r) => ({
        ID: r.id,
        Marchand: r.merchant_business_name ?? "",
        "Code marchand": r.merchant_code ?? "",
        Reference: r.external_reference,
        Billet: (r.parsed_metadata as Record<string, string> | null)?.ticket_number ?? "",
        Devise: r.currency,
        Montant: r.amount_minor / 100,
        Methode: r.payment_method,
        Env: r.livemode ? "reel" : "test",
        Client: [r.customer_first_name, r.customer_last_name].filter(Boolean).join(" "),
        Contact: r.customer_phone || r.customer_email || "",
        Statut: r.status,
        Description: r.failure_message ?? "",
        Date: r.created_at,
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Paiements");
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `paiements-${today}.xlsx`);
    } catch (e) {
      console.error("export failed", e);
      alert("Erreur lors de l'export.");
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<Payment>[] = [
    {
      key: "id",
      header: "ID",
      minWidth: 280,
      cell: (r) => <span className="font-mono text-xs text-[#7e8299]">{r.id}</span>,
    },
    // Colonnes Marchand + Code : visibles UNIQUEMENT pour l'équipe Moko
    // (users cross-tenant, sans merchant_id). Un user marchand ne voit
    // évidemment que sa propre data, donc ces colonnes n'apporteraient rien.
    ...(isMokoStaff
      ? ([
          {
            key: "merchant_name",
            header: "Marchand",
            minWidth: 180,
            cell: (r) => (
              <span className="text-sm text-[#181c32]">{r.merchant_business_name ?? "—"}</span>
            ),
          },
          {
            key: "merchant_code",
            header: "Code marchand",
            minWidth: 160,
            cell: (r) => (
              <span className="font-mono text-xs text-[#7e8299]">{r.merchant_code ?? "—"}</span>
            ),
          },
        ] as ColumnDef<Payment>[])
      : []),
    {
      key: "reference",
      header: "Référence / Billet",
      minWidth: 220,
      accessor: (r) => r.external_reference,
      sortable: true,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="font-mono text-sm text-[#181c32]">{r.external_reference}</div>
          {r.parsed_metadata && (r.parsed_metadata as Record<string, string>).ticket_number && (
            <div className="text-xs text-brand-600 font-semibold mt-0.5">
              Billet: {(r.parsed_metadata as Record<string, string>).ticket_number}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 100,
      sortable: true,
      accessor: (r) => r.amount_minor,
      cell: (r) => <span className="font-semibold text-[#181c32]">{formatAmount(r.amount_minor, r.currency).replace(` ${r.currency}`, "")}</span>,
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 80,
      sortable: true,
      accessor: (r) => r.currency,
      cell: (r) => <span className="text-sm font-semibold text-[#3f4254]">{r.currency}</span>,
    },
    {
      key: "method",
      header: "Méthode",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.payment_method,
      cell: (r) => <Badge color={methodColor(r.payment_method)}>{r.payment_method}</Badge>,
    },
    {
      key: "env",
      header: "Env.",
      minWidth: 80,
      sortable: true,
      accessor: (r) => (r.livemode ? "live" : "test"),
      cell: (r) =>
        r.livemode ? (
          <span
            title="Transaction en environnement réel (débit bancaire effectif)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 border border-emerald-200"
          >
            Réel
          </span>
        ) : (
          <span
            title="Transaction en environnement test (carte virtuelle, aucun débit réel)"
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200"
          >
            Test
          </span>
        ),
    },
    {
      key: "customer",
      header: "Client",
      minWidth: 180,
      wrap: true,
      cell: (r) => (
        <div>
          <div className="text-sm font-medium text-[#181c32]">
            {[r.customer_first_name, r.customer_last_name].filter(Boolean).join(" ") || "—"}
          </div>
          <div className="text-xs text-[#7e8299]">{r.customer_phone || r.customer_email || "—"}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      sortable: true,
      accessor: (r) => r.status,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status}</Badge>,
    },
    {
      key: "description",
      header: "Description",
      minWidth: 240,
      wrap: true,
      cell: (r) => {
        if (!r.failure_message) return <span className="text-xs text-[#a1a5b7]">—</span>;
        const color = r.status === "succeeded" ? "text-success-700" : "text-danger-700";
        return (
          <span className={`text-xs ${color} block max-w-[280px]`} title={r.failure_message}>
            {r.failure_message}
          </span>
        );
      },
    },
    {
      key: "created_at",
      header: "Date",
      minWidth: 130,
      sortable: true,
      accessor: (r) => r.created_at,
      cell: (r) => <span className="text-xs text-[#7e8299]">{formatDate(r.created_at)}</span>,
    },
  ];

  const filters = (
    <>
      <div className="relative">
        <input
          value={ticket}
          onChange={(e) => setTicket(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Numéro de billet (ex: AB1234)"
          className="form-input py-2 px-3 text-sm w-56 uppercase"
        />
      </div>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Tous statuts</option>
        <option value="succeeded">Succeeded</option>
        <option value="processing">Processing</option>
        <option value="pending">Pending</option>
        <option value="failed">Failed</option>
      </select>
      <select value={method} onChange={(e) => setMethod(e.target.value)} className="form-input py-2 px-3 text-sm w-auto">
        <option value="">Toutes méthodes</option>
        <option value="mpesa">M-Pesa</option>
        <option value="orange">Orange</option>
        <option value="airtel">Airtel</option>
        <option value="afrimoney">AfriMoney</option>
        <option value="visa">Visa</option>
        <option value="mastercard">Mastercard</option>
      </select>
      <button onClick={load} className="btn btn-primary btn-sm">Filtrer</button>
      <button
        onClick={handleExport}
        disabled={exporting || payments.length === 0}
        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 hover:bg-brand-800 disabled:bg-brand-300 disabled:cursor-not-allowed text-white px-3 py-2 text-sm font-semibold transition-colors"
        title="Exporter en Excel (respecte les filtres actifs)"
      >
        <Download size={14} />
        {exporting ? "Export…" : "Exporter Excel"}
      </button>
    </>
  );

  return (
    <div>
      <PageHeader
        title="Paiements"
        breadcrumbs={[{ label: "Plateforme" }, { label: "Paiements" }]}
      />

      <DataTable<Payment>
        title="Transactions"
        subtitle="Recherche par numéro de billet, statut, ou méthode"
        searchable
        searchPlaceholder="Rechercher dans les résultats…"
        columns={columns}
        data={payments}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/payments/${r.id}`)}
        loading={loading}
        filtersSlot={filters}
        emptyMessage="Aucun paiement trouvé"
        initialSort={{ key: "created_at", dir: "desc" }}
      />
    </div>
  );
}

function methodColor(method: string): "primary" | "info" | "warning" | "light" {
  if (method === "mpesa" || method === "afrimoney") return "primary";
  if (method === "orange") return "warning";
  if (method === "airtel") return "info";
  return "light";
}
